/**
 * Map graph queries: who borders whom, what may be attacked, where armies may
 * be fortified to.
 *
 * The engine takes the map structurally rather than importing `@risk/maps`, so
 * the rules stay independent of the map package and any graph of this shape --
 * including a custom map uploaded at runtime -- works.
 */

import type { ContinentId, SeatId, TerritoryId } from '../ids.js';
import type { RulesConfig } from './config.js';

export interface MapGraphInput {
  readonly territories: ReadonlyArray<{
    readonly id: TerritoryId;
    readonly continent: ContinentId;
    readonly adjacent: readonly TerritoryId[];
  }>;
  readonly continents: ReadonlyArray<{
    readonly id: ContinentId;
    readonly bonus: number;
    readonly territories: readonly TerritoryId[];
  }>;
}

export interface ContinentIndex {
  readonly bonus: number;
  readonly territories: readonly TerritoryId[];
}

export interface MapIndex {
  readonly territoryIds: readonly TerritoryId[];
  readonly adjacency: ReadonlyMap<TerritoryId, readonly TerritoryId[]>;
  readonly continentOf: ReadonlyMap<TerritoryId, ContinentId>;
  readonly continents: ReadonlyMap<ContinentId, ContinentIndex>;
}

/** Build O(1) lookups once, rather than scanning arrays on every rules check. */
export function indexMap(map: MapGraphInput): MapIndex {
  const adjacency = new Map<TerritoryId, readonly TerritoryId[]>();
  const continentOf = new Map<TerritoryId, ContinentId>();
  for (const territory of map.territories) {
    adjacency.set(territory.id, territory.adjacent);
    continentOf.set(territory.id, territory.continent);
  }

  const continents = new Map<ContinentId, ContinentIndex>();
  for (const continent of map.continents) {
    continents.set(continent.id, { bonus: continent.bonus, territories: continent.territories });
  }

  return {
    territoryIds: map.territories.map((t) => t.id),
    adjacency,
    continentOf,
    continents,
  };
}

export function areAdjacent(index: MapIndex, a: TerritoryId, b: TerritoryId): boolean {
  return index.adjacency.get(a)?.includes(b) ?? false;
}

export function neighboursOf(index: MapIndex, id: TerritoryId): readonly TerritoryId[] {
  return index.adjacency.get(id) ?? [];
}

/** Territories that may be attacked from `from`: bordering, and held by someone else. */
export function attackTargets(
  index: MapIndex,
  from: TerritoryId,
  ownerOf: ReadonlyMap<TerritoryId, SeatId | null>,
): readonly TerritoryId[] {
  const attacker = ownerOf.get(from) ?? null;
  return neighboursOf(index, from).filter((id) => ownerOf.get(id) !== attacker);
}

/**
 * Every territory reachable from `start` without leaving `owner`'s territory.
 * Includes `start` itself.
 */
export function connectedOwnedTerritories(
  index: MapIndex,
  start: TerritoryId,
  owner: SeatId,
  ownerOf: ReadonlyMap<TerritoryId, SeatId | null>,
): ReadonlySet<TerritoryId> {
  const seen = new Set<TerritoryId>();
  if (ownerOf.get(start) !== owner) return seen;

  seen.add(start);
  const queue: TerritoryId[] = [start];
  while (queue.length > 0) {
    const current = queue.pop()!;
    for (const neighbour of neighboursOf(index, current)) {
      if (seen.has(neighbour) || ownerOf.get(neighbour) !== owner) continue;
      seen.add(neighbour);
      queue.push(neighbour);
    }
  }
  return seen;
}

/** Where a fortifying move from `from` may land, under the configured style. */
export function fortifyTargets(
  index: MapIndex,
  from: TerritoryId,
  owner: SeatId,
  ownerOf: ReadonlyMap<TerritoryId, SeatId | null>,
  rules: RulesConfig,
): readonly TerritoryId[] {
  if (ownerOf.get(from) !== owner) return [];

  if (rules.fortify.style === 'adjacent') {
    return neighboursOf(index, from).filter((id) => ownerOf.get(id) === owner);
  }
  const reachable = connectedOwnedTerritories(index, from, owner, ownerOf);
  return [...reachable].filter((id) => id !== from);
}

/** Continents where `owner` holds every territory. */
export function continentsHeldBy(
  index: MapIndex,
  owner: SeatId,
  ownerOf: ReadonlyMap<TerritoryId, SeatId | null>,
): readonly ContinentId[] {
  const held: ContinentId[] = [];
  for (const [id, continent] of index.continents) {
    if (continent.territories.every((t) => ownerOf.get(t) === owner)) held.push(id);
  }
  return held;
}

export function territoriesHeldBy(
  index: MapIndex,
  owner: SeatId,
  ownerOf: ReadonlyMap<TerritoryId, SeatId | null>,
): readonly TerritoryId[] {
  return index.territoryIds.filter((id) => ownerOf.get(id) === owner);
}
